import { useMsal } from '@azure/msal-react'

import React, { useState } from 'react'

import Button from '../Common/Button'

type ErrorBoundaryMessageProps = {
  fullPage?: boolean
  error: Error
  resetError: () => void
  showSupportOption?: boolean
}

const ErrorBoundaryMessage: React.FC<ErrorBoundaryMessageProps> = ({ fullPage = false, error, resetError, showSupportOption = true }) => {
  const { instance } = useMsal()
  const [showDetails, setShowDetails] = useState(false)
  const [submittingTicket, setSubmittingTicket] = useState(false)
  const [ticketSubmitted, setTicketSubmitted] = useState(false)

  const handleSubmitTicket = async () => {
    setSubmittingTicket(true)
    try {
      // Example implementation - replace with your actual ticket submission logic
      // await submitErrorTicket({
      //   errorMessage: error.message,
      //   errorStack: error.stack,
      //   userInfo: instance.getActiveAccount(),
      //   url: window.location.href,
      // });

      // Simulate API call for demo purposes
      await new Promise((resolve) => setTimeout(resolve, 1000))

      setTicketSubmitted(true)
    } catch (e) {
      console.error('Failed to submit ticket:', e)
    } finally {
      setSubmittingTicket(false)
    }
  }

  const containerClass = fullPage
    ? 'fixed inset-0 z-50 bg-th-content bg-opacity-95 flex items-center justify-center p-4'
    : 'h-full w-full flex items-center justify-center p-4 bg-th-content'

  return (
    <div className={containerClass}>
      <div className="bg-th-background w-full max-w-md rounded-lg p-6 shadow-lg">
        <h2 className="mb-2 text-xl font-semibold text-th-text">Something went wrong</h2>

        <p className="mb-4 text-th-text-secondary">An unexpected error occurred while rendering this component.</p>

        {/* Error details section */}
        <div className="mb-6">
          <button onClick={() => setShowDetails(!showDetails)} className="text-th-accent mb-2 flex items-center text-sm">
            {showDetails ? 'Hide' : 'Show'} error details
            <svg className={`ml-1 h-4 w-4 transform ${showDetails ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showDetails && (
            <div className="max-h-40 overflow-auto rounded bg-th-content-secondary p-3 text-sm">
              <p className="font-semibold">
                {error.name}: {error.message}
              </p>
              {error.stack && <pre className="mt-2 whitespace-pre-wrap text-xs text-th-text-secondary">{error.stack}</pre>}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3">
          <Button onClick={resetError} variant="primary">
            Try Again
          </Button>

          <Button onClick={() => window.location.reload()} variant="secondary">
            Reload Page
          </Button>

          <Button onClick={() => instance.logout()} variant="secondary">
            Return to Login
          </Button>

          {showSupportOption && !ticketSubmitted && (
            <Button onClick={handleSubmitTicket} variant="secondary" disabled={submittingTicket}>
              {submittingTicket ? 'Submitting...' : 'Report Issue'}
            </Button>
          )}

          {ticketSubmitted && <div className="mt-2 w-full rounded bg-green-100 p-2 text-sm text-green-800">Issue reported. Thank you!</div>}
        </div>
      </div>
    </div>
  )
}

export default ErrorBoundaryMessage
